/**
 * Tenant-wide "Add Lead" required-fields helpers. The server stores a list of
 * field KEYS ("basic:email", "source:branch", "pref:<id>", …); Name + Mobile are
 * always required by the core validator, so they're folded in here too. Shared by
 * the form's submit guard (FormLeadContext) and the field asterisks (LeadFormFields).
 */

/** Built-in key → { form data field, human label }. */
export const REQUIRED_BUILTIN: Record<string, { field: string; label: string }> = {
  "basic:name": { field: "name", label: "Full Name" },
  "basic:mobile": { field: "phone", label: "Mobile" },
  "basic:email": { field: "email", label: "Email" },
  "basic:gender": { field: "gender", label: "Gender" },
  "source:branch": { field: "branchId", label: "Branch" },
  "source:leadSource": { field: "leadSourceId", label: "Lead Source" },
  "source:campaign": { field: "campaignId", label: "Campaign" },
  "source:user": { field: "userId", label: "Assigned User" },
};

const hasVal = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  return true;
};

/**
 * Server-required set + the always-on Name/Mobile (enforced by the core
 * validator), minus any field Customize Add Lead hides: a hidden field is not
 * on the form, so it can't be required (the server skips it the same way).
 */
export function effectiveRequiredKeys(
  serverRequired: string[],
  hidden: string[] = [],
): Set<string> {
  const hiddenSet = new Set(hidden);
  return new Set([
    ...serverRequired.filter((key) => !hiddenSet.has(key)),
    "basic:name",
    "basic:mobile",
  ]);
}

/** Human labels of the required fields the form data hasn't filled. */
export function missingRequiredFields(
  data: any,
  required: Set<string>,
  prefLabel: (id: string) => string,
  /** Custom-field ids the form can actually render. A required `pref:<id>`
   *  outside it is stale (field deleted) — nobody can fill it, so skip it. */
  livePrefIds?: Set<string>,
): string[] {
  const missing: string[] = [];
  for (const key of required) {
    if (key.startsWith("pref:")) {
      const id = key.slice(5);
      if (livePrefIds && !livePrefIds.has(id)) continue;
      const scope = Array.isArray(data?.customInputScopes)
        ? data.customInputScopes.find((s: any) => String(s?.customInputId) === id)
        : null;
      const ok = !!scope && (hasVal(scope.textValue) || hasVal(scope.optionIds));
      if (!ok) missing.push(prefLabel(id));
      continue;
    }
    const def = REQUIRED_BUILTIN[key];
    if (def && !hasVal(data?.[def.field])) missing.push(def.label);
  }
  return missing;
}
